/**
 * Tenant profile form (ADR-0036 Fase 2) — billing/anagrafica + logo. PRESENTATIONAL:
 * it takes the current value + callbacks, so the SAME form serves the operator's own
 * tenant (Impostazioni → Profilo azienda) AND the superadmin's per-tenant drawer. The
 * logo arrives as a `data:` URL on `value.logo_url`.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { TenantProfile, TenantProfileIn } from "@/lib/tenantProfile";

interface Props {
  value: TenantProfile | undefined;
  loading?: boolean;
  /** When true the form is view-only (no write permission). */
  readOnly?: boolean;
  saving?: boolean;
  uploadingLogo?: boolean;
  onSave: (body: TenantProfileIn) => void;
  onUploadLogo: (file: File) => void;
  onDeleteLogo: () => void;
}

const EMPTY: TenantProfileIn = {};

function toForm(v: TenantProfile | undefined): TenantProfileIn {
  if (!v) return { ...EMPTY };
  return {
    entity_type: v.entity_type ?? "",
    legal_name: v.legal_name ?? "",
    vat_number: v.vat_number ?? "",
    tax_code: v.tax_code ?? "",
    pec: v.pec ?? "",
    sdi_code: v.sdi_code ?? "",
    billing_email: v.billing_email ?? "",
    external_billing_id: v.external_billing_id ?? "",
    notes: v.notes ?? "",
    address: { ...(v.address ?? {}) },
    contact: { ...(v.contact ?? {}) },
  };
}

function Field({
  label,
  value,
  onChange,
  disabled,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string | null | undefined;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5">
      <h3 className="mb-4 text-sm font-semibold text-foreground">{title}</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export function TenantProfileForm({
  value,
  loading,
  readOnly,
  saving,
  uploadingLogo,
  onSave,
  onUploadLogo,
  onDeleteLogo,
}: Props) {
  const { t } = useTranslation();
  const [form, setForm] = React.useState<TenantProfileIn>(() => toForm(value));
  const fileRef = React.useRef<HTMLInputElement>(null);

  // Re-seed when a fresh value arrives (e.g. switching tenants in the drawer).
  React.useEffect(() => {
    setForm(toForm(value));
  }, [value]);

  const set = (key: keyof TenantProfileIn, v: string) => setForm((f) => ({ ...f, [key]: v }));
  const setAddr = (key: string, v: string) =>
    setForm((f) => ({ ...f, address: { ...(f.address ?? {}), [key]: v } }));
  const setContact = (key: string, v: string) =>
    setForm((f) => ({ ...f, contact: { ...(f.contact ?? {}), [key]: v } }));

  if (loading) {
    return <p className="text-sm text-muted-foreground">{t("common.loading", "Caricamento…")}</p>;
  }

  return (
    <div className="space-y-5">
      {/* Logo */}
      <div className="rounded-lg border border-border bg-surface p-5">
        <h3 className="mb-1 text-sm font-semibold text-foreground">
          {t("tenantProfile.logo.title", "Logo")}
        </h3>
        <p className="mb-4 text-xs text-muted-foreground">
          {t(
            "tenantProfile.logo.hint",
            "Mostrato nell'header del pannello del tenant. PNG/JPG/SVG, max 1 MB.",
          )}
        </p>
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-md border border-border bg-background">
            {value?.logo_url ? (
              <img src={value.logo_url} alt="logo" className="max-h-16 max-w-16 object-contain" />
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUploadLogo(f);
                  e.target.value = "";
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploadingLogo}
                onClick={() => fileRef.current?.click()}
              >
                {uploadingLogo
                  ? t("tenantProfile.logo.uploading", "Caricamento…")
                  : t("tenantProfile.logo.upload", "Carica logo")}
              </Button>
              {value?.has_logo && (
                <Button type="button" variant="ghost" size="sm" onClick={onDeleteLogo}>
                  {t("tenantProfile.logo.remove", "Rimuovi")}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <Group title={t("tenantProfile.group.company", "Anagrafica")}>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">
            {t("tenantProfile.entityType", "Tipo di cliente")}
          </Label>
          <select
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm disabled:opacity-50"
            value={form.entity_type ?? ""}
            disabled={readOnly}
            onChange={(e) => set("entity_type", e.target.value)}
          >
            <option value="">{t("tenantProfile.entityUnset", "Non specificato")}</option>
            <option value="business">{t("tenantProfile.entityBusiness", "Azienda")}</option>
            <option value="private">{t("tenantProfile.entityPrivate", "Privato")}</option>
          </select>
        </div>
        <Field
          label={t("tenantProfile.legalName", "Ragione sociale")}
          value={form.legal_name}
          onChange={(v) => set("legal_name", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.vat", "Partita IVA")}
          value={form.vat_number}
          onChange={(v) => set("vat_number", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.taxCode", "Codice fiscale")}
          value={form.tax_code}
          onChange={(v) => set("tax_code", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.billingEmail", "Email fatturazione")}
          type="email"
          value={form.billing_email}
          onChange={(v) => set("billing_email", v)}
          disabled={readOnly}
        />
      </Group>

      <Group title={t("tenantProfile.group.einvoice", "Fatturazione elettronica")}>
        <Field
          label={t("tenantProfile.pec", "PEC")}
          type="email"
          value={form.pec}
          onChange={(v) => set("pec", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.sdi", "Codice destinatario (SDI)")}
          value={form.sdi_code}
          onChange={(v) => set("sdi_code", v)}
          disabled={readOnly}
        />
      </Group>

      <Group title={t("tenantProfile.group.address", "Indirizzo")}>
        <Field
          label={t("tenantProfile.street", "Via")}
          value={form.address?.street}
          onChange={(v) => setAddr("street", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.city", "Città")}
          value={form.address?.city}
          onChange={(v) => setAddr("city", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.zip", "CAP")}
          value={form.address?.zip}
          onChange={(v) => setAddr("zip", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.province", "Provincia")}
          value={form.address?.province}
          onChange={(v) => setAddr("province", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.country", "Paese")}
          value={form.address?.country}
          onChange={(v) => setAddr("country", v)}
          disabled={readOnly}
        />
      </Group>

      <Group title={t("tenantProfile.group.contact", "Referente")}>
        <Field
          label={t("tenantProfile.contactName", "Nome")}
          value={form.contact?.name}
          onChange={(v) => setContact("name", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.contactPhone", "Telefono")}
          value={form.contact?.phone}
          onChange={(v) => setContact("phone", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.contactEmail", "Email")}
          type="email"
          value={form.contact?.email}
          onChange={(v) => setContact("email", v)}
          disabled={readOnly}
        />
        <Field
          label={t("tenantProfile.externalId", "ID cliente esterno")}
          value={form.external_billing_id}
          onChange={(v) => set("external_billing_id", v)}
          disabled={readOnly}
        />
      </Group>

      <div className="rounded-lg border border-border bg-surface p-5">
        <Label className="text-xs text-muted-foreground">{t("tenantProfile.notes", "Note")}</Label>
        <textarea
          className="mt-1 min-h-20 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={form.notes ?? ""}
          disabled={readOnly}
          onChange={(e) => set("notes", e.target.value)}
        />
      </div>

      {!readOnly && (
        <div className="flex justify-end">
          <Button type="button" disabled={saving} onClick={() => onSave(form)}>
            {saving ? t("common.saving", "Salvataggio…") : t("common.save", "Salva")}
          </Button>
        </div>
      )}
    </div>
  );
}
